import {
  BillingRegistryTrialNoticeKind as Kind,
  BillingRegistryTrialNoticeOperation as Operation,
  type BillingRegistryTrialNotice,
  type BillingRegistryTrialNoticeResponse
} from '@tryiris-ai/common.protojs/api/v1/billing_registry';
import { AgentRegistryMembershipRole as Role } from '@tryiris-ai/common.protojs/api/v1/agent_registry';
import type { TrialNoticeBillingRead } from './limits.js';

export interface TrialNoticeContext {
  readonly roomID: string;
  readonly actorID: string | null;
  readonly role: Role | undefined;
  readonly ready: boolean;
  readonly billing: TrialNoticeBillingRead;
}

export interface DisplayedTrialNotice {
  readonly roomID: string;
  readonly actorID: string;
  readonly notice: BillingRegistryTrialNotice;
  readonly receivedAt: number;
}

/** Only permission whose first publication has never been attempted. */
export interface UnpublishedTrialNoticePermission {
  readonly identity: string;
  readonly actorID: string;
  readonly roomID: string;
  readonly displayId: string;
  readonly deadline: number | undefined;
}

interface NoticePorts {
  readonly observationKind?: Kind;
  readonly observe?: (notice: BillingRegistryTrialNotice | null, receivedAt: number) => void;
  readonly request: (
    roomID: string,
    operation: Operation,
    kind: Kind | undefined,
    commandId: string,
    displayId?: string
  ) => Promise<BillingRegistryTrialNoticeResponse>;
  readonly canDisplay: () => boolean;
  readonly monotonicNow: () => number;
  readonly uuid: () => string;
  readonly publish: (view: DisplayedTrialNotice | null) => void;
  readonly unpublishedPermissions?: Map<string, UnpublishedTrialNoticePermission>;
  readonly unpublishedPermissionListeners?: Set<
    (permission: UnpublishedTrialNoticePermission) => void
  >;
}

/** A single Studio consumer of the server's one-use display protocol. */
export class StandardTrialNoticeController {
  private context: TrialNoticeContext | undefined;
  private generation = 0;
  private disposed = false;
  private visible: DisplayedTrialNotice | null = null;
  private readonly paidRooms = new Set<string>();
  private readonly attempted = new Set<string>();
  private selectedIdentity: string | undefined;
  private pendingDisplay: UnpublishedTrialNoticePermission | undefined;
  private readonly unpublishedPermissions: Map<string, UnpublishedTrialNoticePermission>;
  private pendingShown: { notice: BillingRegistryTrialNotice; attempts: number } | undefined;
  private inFlight: { generation: number; promise: Promise<void> } | undefined;

  private readonly permissionAvailable = (permission: UnpublishedTrialNoticePermission): void => {
    // An old route can receive permission after its replacement's first read
    // has finished. Wake that consumer after its current read, so coalescing
    // cannot attach recovery to a decision that never saw the permission.
    const resume = () => {
      const c = this.context;
      if (this.disposed) return;
      if (this.paidRooms.has(permission.roomID)) {
        this.unpublishedPermissions.delete(permission.identity);
        return;
      }
      if (
        c?.actorID === permission.actorID &&
        c.roomID === permission.roomID &&
        this.unpublishedPermissions.get(permission.identity) === permission
      )
        void this.refresh();
    };
    void (this.inFlight?.promise ?? Promise.resolve()).then(resume, resume);
  };

  constructor(private readonly ports: NoticePorts) {
    this.unpublishedPermissions = ports.unpublishedPermissions ?? new Map();
  }

  setContext(context: TrialNoticeContext): Promise<void> {
    const previous = this.context;
    this.context = context;
    this.generation++;
    if (previous?.roomID !== context.roomID || previous?.actorID !== context.actorID) {
      this.selectedIdentity = undefined;
      this.clearPendingDisplay();
      this.pendingShown = undefined;
      this.close();
    }
    // A published warning cannot reattach when an unresolved role settles.
    // Never-published permissions remain retained for a fresh role check.
    if (
      this.visible &&
      (context.role === undefined ||
        context.role === Role.AGENT_REGISTRY_MEMBERSHIP_ROLE_UNSPECIFIED)
    )
      this.close();
    return this.refresh();
  }

  /** A durable CONFIRMED receipt wins before payment-return refresh finishes. */
  confirmedPaid(roomID: string): void {
    this.paidRooms.add(roomID);
    if (roomID === this.context?.roomID) this.ports.observe?.(null, this.ports.monotonicNow());
    // A new route must not recover unused permission from before payment.
    for (const [identity, permission] of this.unpublishedPermissions) {
      if (permission.roomID === roomID) this.unpublishedPermissions.delete(identity);
    }
    if (roomID === this.context?.roomID) {
      this.generation++;
      this.selectedIdentity = undefined;
      this.clearPendingDisplay();
      this.pendingShown = undefined;
      this.close();
    }
  }

  private canShow(kind: Kind): boolean {
    const c = this.context;
    if (
      !c?.ready ||
      !c.actorID ||
      c.role === undefined ||
      c.role === Role.AGENT_REGISTRY_MEMBERSHIP_ROLE_UNSPECIFIED ||
      this.paidRooms.has(c.roomID) ||
      !this.ports.canDisplay()
    )
      return false;
    const read = c.billing;
    if (
      read.roomID !== c.roomID ||
      read.status !== 'loaded' ||
      read.response?.billingStateKnown !== true
    )
      return false;
    if (kind === Kind.TRIAL_NOTICE_EXPIRY) {
      return (
        read.response.trialCameraRestrictions === true && read.response.limits?.maxSources === 1
      );
    }
    if (kind === Kind.TRIAL_NOTICE_WELCOME) return read.response.trialEnd > 0;
    return kind === Kind.TRIAL_NOTICE_REMINDER;
  }

  private publish(view: DisplayedTrialNotice | null): void {
    this.visible = view;
    this.ports.publish(view);
  }

  private close(): void {
    if (this.visible !== null) this.publish(null);
  }

  private clearPendingDisplay(): void {
    // Route/context changes drop this controller's reference, without losing a
    // known unused permission or manufacturing a shown receipt.
    this.pendingDisplay = undefined;
  }

  private pruneUnpublishedPermissions(): void {
    const now = this.ports.monotonicNow();
    for (const [identity, permission] of this.unpublishedPermissions) {
      if (permission.deadline !== undefined && permission.deadline <= now)
        this.unpublishedPermissions.delete(identity);
    }
  }

  private windowRemainingMs(
    notice: BillingRegistryTrialNotice,
    requestedAt: number
  ): number | null {
    const elapsedMs = this.ports.monotonicNow() - requestedAt;
    if (
      !Number.isSafeInteger(notice.windowEnd) ||
      notice.windowEnd <= 0 ||
      !Number.isSafeInteger(notice.serverNowMs) ||
      notice.serverNowMs <= 0 ||
      !Number.isFinite(elapsedMs) ||
      elapsedMs < 0
    )
      return null;
    return notice.windowEnd * 1000 - notice.serverNowMs - elapsedMs;
  }

  dismiss(): void {
    this.close();
  }

  /** React effect replay resumes the instance without renewing a used permit. */
  activate(): void {
    this.disposed = false;
    // Register only committed consumers; React may discard a constructor or
    // replay this effect without leaving a listener for an abandoned instance.
    this.ports.unpublishedPermissionListeners?.add(this.permissionAvailable);
  }

  dispose(): void {
    this.disposed = true;
    this.ports.unpublishedPermissionListeners?.delete(this.permissionAvailable);
    this.generation++;
    this.close();
  }

  /** Called only by the painted dialog or an interaction with that dialog. */
  reportShown(displayId: string): void {
    const notice = this.visible?.notice;
    if (!notice || notice.displayId !== displayId || this.pendingShown) return;
    this.pendingShown = { notice, attempts: 0 };
    void this.flushShown();
  }

  private async flushShown(): Promise<void> {
    const pending = this.pendingShown;
    const c = this.context;
    if (!pending || !c || pending.attempts >= 3 || this.paidRooms.has(c.roomID)) return;
    pending.attempts++;
    try {
      const reply = await this.ports.request(
        c.roomID,
        Operation.TRIAL_NOTICE_REPORT_SHOWN,
        pending.notice.kind,
        this.ports.uuid(),
        pending.notice.displayId
      );
      if (
        !reply.error &&
        (reply.notice?.shownRecorded || reply.notice?.eligible === false) &&
        this.pendingShown === pending
      )
        this.pendingShown = undefined;
      if (!reply.error && reply.notice?.everConfirmedPaid) this.confirmedPaid(c.roomID);
    } catch {
      // Only this actual-display receipt may retry. Never obtain a new display
      // permit after an uncertain outcome (settled in-app warning policy).
    } finally {
      // An exhausted actual-display receipt cannot block the other lifecycle
      // notice. Its server authorization stays terminal; no display is renewed.
      if (this.pendingShown === pending && pending.attempts >= 3) this.pendingShown = undefined;
    }
  }

  refresh(): Promise<void> {
    this.pruneUnpublishedPermissions();
    const c = this.context;
    if (!c || this.disposed || !c.actorID || this.paidRooms.has(c.roomID)) return Promise.resolve();
    // A poll cannot invalidate an earlier same-context read. Coalescing also
    // bounds requests when a primary observation takes longer than one tick.
    if (this.inFlight?.generation === this.generation) return this.inFlight.promise;
    const generation = this.generation;
    const promise = this.observe(c, generation).finally(() => {
      if (this.inFlight?.promise === promise) this.inFlight = undefined;
    });
    this.inFlight = { generation, promise };
    return promise;
  }

  private async observe(c: TrialNoticeContext, generation: number): Promise<void> {
    if (!c.actorID) return;
    const current = () =>
      !this.disposed &&
      generation === this.generation &&
      this.context?.roomID === c.roomID &&
      this.context.actorID === c.actorID &&
      !this.paidRooms.has(c.roomID);
    // A known display may retry its idempotent receipt after X/Close, including
    // during a failed limit read. This cannot authorize or render another notice.
    await this.flushShown();
    if (!current()) return;
    // Visible notices still observe cancellation during a failed billing read.
    if (
      !this.visible &&
      (this.ports.observationKind === Kind.TRIAL_NOTICE_WELCOME
        ? !this.canShow(Kind.TRIAL_NOTICE_WELCOME)
        : !this.canShow(Kind.TRIAL_NOTICE_REMINDER) && !this.canShow(Kind.TRIAL_NOTICE_EXPIRY))
    )
      return;
    try {
      const requestedAt = this.ports.monotonicNow();
      const reply = await this.ports.request(
        c.roomID,
        Operation.TRIAL_NOTICE_OBSERVE,
        this.ports.observationKind,
        this.ports.uuid()
      );
      // Confirmed-paid authority is monotonic for the requested Studio, even
      // if a newer context/read has superseded this response's Free details.
      if (!reply.error && reply.notice?.everConfirmedPaid) {
        this.confirmedPaid(c.roomID);
        return;
      }
      if (!current()) return;
      if (reply.error || !reply.notice) {
        // A failed welcome read cannot keep claiming current entitlement from an older summary.
        this.ports.observe?.(null, requestedAt);
        return;
      }
      const observed = reply.notice;
      this.ports.observe?.(observed, requestedAt);
      if (this.visible) {
        if (!observed.eligible || observed.kind !== this.visible.notice.kind) this.close();
        else {
          // Refreshes retain request transit time too. An unresolved clock leaves
          // the previous trusted deadline intact; it cannot extend this display.
          const remaining = this.windowRemainingMs(observed, requestedAt);
          if (remaining !== null) {
            if (remaining <= 0) this.close();
            else
              this.publish({
                ...this.visible,
                notice: { ...observed, displayId: this.visible.notice.displayId },
                receivedAt: requestedAt
              });
          } else if (observed.canUpgrade !== true && this.visible.notice.canUpgrade === true) {
            // An invalid refresh clock cannot extend the trusted deadline, but
            // explicit ownership revocation must still remove billing actions.
            this.publish({
              ...this.visible,
              notice: { ...this.visible.notice, canUpgrade: false }
            });
          }
        }
        return;
      }
      if (!observed.eligible || !this.canShow(observed.kind)) return;
      const identity = `${c.actorID}/${c.roomID}/${observed.kind === Kind.TRIAL_NOTICE_WELCOME ? observed.trialId : observed.trialEnd}/${observed.kind}`;
      // Fence older lifecycle replies before awaiting a new authorization,
      // including the interval when its permission has not arrived yet.
      this.selectedIdentity = identity;
      let pending = this.unpublishedPermissions.get(identity);
      this.pendingDisplay = pending;
      if (pending?.identity !== identity) {
        // Select only the current lifecycle before reserving expiry. A late
        // warning cannot replace this controller's newer retained record or
        // pass the final exact-record check.
        this.clearPendingDisplay();
        if (!observed.available || this.attempted.has(identity)) return;
        this.attempted.add(identity);
        const authorized = await this.ports.request(
          c.roomID,
          Operation.TRIAL_NOTICE_AUTHORIZE_DISPLAY,
          observed.kind,
          this.ports.uuid()
        );
        if (!authorized.error && authorized.notice?.everConfirmedPaid) {
          this.confirmedPaid(c.roomID);
          return;
        }
        // Explicit rollback can recover through fresh primary availability.
        // A lost authorization response still cannot create a second display.
        if (authorized.error) {
          this.attempted.delete(identity);
          return;
        }
        const contextMatches =
          this.context?.roomID === c.roomID && this.context.actorID === c.actorID;
        if (
          !authorized.notice?.displayId ||
          !authorized.notice.eligible ||
          authorized.notice.kind !== observed.kind ||
          authorized.notice.trialEnd !== observed.trialEnd ||
          (contextMatches && this.selectedIdentity !== identity) ||
          this.paidRooms.has(c.roomID)
        )
          return;
        // This app knows publication has never been attempted. Retain that
        // exact permission before rejecting an obsolete billing generation;
        // a later primary check may make its first display, never a retry.
        if (contextMatches && this.pendingDisplay && this.pendingDisplay.identity !== identity)
          return;
        const remaining = this.windowRemainingMs(observed, requestedAt);
        pending = {
          identity,
          actorID: c.actorID,
          roomID: c.roomID,
          displayId: authorized.notice.displayId,
          deadline: remaining === null ? undefined : this.ports.monotonicNow() + remaining
        };
        this.unpublishedPermissions.set(identity, pending);
        if (contextMatches) this.pendingDisplay = pending;
        // Other mounted routes use the same exact unused record. Notification
        // starts a fresh authority check; it never publishes or acknowledges it.
        for (const listener of this.ports.unpublishedPermissionListeners ?? []) {
          if (listener !== this.permissionAvailable) listener(pending);
        }
        if (!current()) {
          // The newer check may already have decided without this permission,
          // while its promise still occupies inFlight. Resume only after it
          // settles; coalescing onto that completed decision loses the check.
          const permit = pending;
          const resume = () => {
            if (
              this.pendingDisplay === permit &&
              !this.disposed &&
              this.context?.roomID === c.roomID &&
              this.context.actorID === c.actorID &&
              !this.paidRooms.has(c.roomID)
            )
              void this.refresh();
          };
          void (this.inFlight?.promise ?? Promise.resolve()).then(resume, resume);
          return;
        }
      }
      // Payment may complete while the authorization reply is in flight. A
      // fresh primary observation fences it before rendering the one-use permit.
      const finalRequestedAt = this.ports.monotonicNow();
      const final = await this.ports.request(
        c.roomID,
        Operation.TRIAL_NOTICE_OBSERVE,
        observed.kind,
        this.ports.uuid()
      );
      if (!final.error && final.notice?.everConfirmedPaid) {
        this.confirmedPaid(c.roomID);
        return;
      }
      if (
        !current() ||
        this.pendingDisplay !== pending ||
        this.unpublishedPermissions.get(identity) !== pending ||
        final.error ||
        !final.notice
      )
        return;
      if (observed.kind === Kind.TRIAL_NOTICE_WELCOME) {
        this.ports.observe?.(final.notice, finalRequestedAt);
        // Another device's receipt or a replacement enrollment invalidates this unused permission.
        if (
          !final.notice.available ||
          final.notice.shownRecorded ||
          final.notice.trialId !== observed.trialId
        ) {
          this.unpublishedPermissions.delete(identity);
          this.clearPendingDisplay();
          return;
        }
      }
      if (
        !final.notice.eligible ||
        final.notice.kind !== observed.kind ||
        final.notice.trialEnd !== observed.trialEnd ||
        !this.canShow(observed.kind)
      )
        return;
      // Only server time qualifies the notice. Subtracting the full request
      // duration conservatively rejects a reply that arrived after its window;
      // a local wall clock never creates eligibility (trial notice timing policy).
      const remaining = this.windowRemainingMs(final.notice, finalRequestedAt);
      if (remaining === null || remaining <= 0) return;
      // Clear before publication so overlapping reads and uncertain render
      // outcomes cannot replay this permission (state-machine primitives rule).
      this.unpublishedPermissions.delete(identity);
      this.clearPendingDisplay();
      this.publish({
        roomID: c.roomID,
        actorID: c.actorID,
        notice: { ...final.notice, displayId: pending.displayId },
        receivedAt: finalRequestedAt
      });
    } catch {
      if (current()) this.ports.observe?.(null, this.ports.monotonicNow());
      // Observation can recover on focus/poll. A new authorization still needs
      // fresh primary availability. A known never-published permission is kept;
      // an uncertain publication cannot be retried from this controller.
    }
  }
}
